import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Card } from "@feed/shared";
import { relativeTime } from "../lib/relative";
import { blockStyle } from "../lib/types";
import { ChevronEndIcon } from "./icons";

export function itemsText(t: (key: string, options?: Record<string, unknown>) => string, count: number): string {
  return count === 1 ? t("feed.itemOne") : t("feed.itemMany", { count });
}

interface CardBlockProps {
  card: Card;
  onOpen: () => void;
  // Shown instead of the creation time, e.g. "Saved yesterday".
  timeLabel?: (when: string) => string;
  // Buttons under the block (Unsave / Dismiss on the Saved screen).
  actions?: ReactNode;
}

// One card as a large colour block. Every pushed string carries dir="auto", so a Hebrew
// summary aligns right even when the UI is English.
export function CardBlock({ card, onOpen, timeLabel, actions }: CardBlockProps) {
  const { t, i18n } = useTranslation();
  const when = relativeTime(card.status === "saved" && timeLabel ? card.status_changed_at : card.created_at, new Date(), i18n.language);
  const unread = card.status === "unread";

  return (
    <article className={`block block-${blockStyle(card.type)}`}>
      <button type="button" className="block-main" onClick={onOpen}>
        <span className="block-top">
          <span className="badge" dir="auto">
            {card.type}
          </span>
          <span className="block-time">
            {timeLabel ? timeLabel(when) : when}
            {unread && <span className="unread-dot" role="img" aria-label={t("feed.unreadMarker")} />}
          </span>
        </span>
        <span className="block-summary" dir="auto">
          {card.summary}
        </span>
        <span className="block-foot">
          <span>{itemsText(t, card.items.length)}</span>
          <ChevronEndIcon />
        </span>
      </button>
      {actions && <div className="block-actions">{actions}</div>}
    </article>
  );
}
